import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { has, forEach } from 'lodash';
import { share, map, filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';

import { TicketViewService } from './ticket-view.service';
import { BrigadePopoverService } from '../../../shared/popovers/brigade-popover/brigade-popover.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Enums } from '../../../configs/Enums';
import { TICKETS_PATHS } from '../tickets';
import { catchError } from 'rxjs/operators';
import { CheckListsApiService } from '../../../services/check-lists-api.service';
import { IChecklistTicket } from '../../check-lists-page/interfaces/checklist-ticket.interface';
import { IChecklistTicketAnswer } from '../../check-lists-page/interfaces/checklist-ticket-answer.interface';
import { HttpResponse } from '@angular/common/http';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-ticket-view',
  templateUrl: './ticket-view.component.html',
  styleUrls: ['./ticket-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TicketViewComponent implements OnInit, OnDestroy {
  public moment = require('moment');
  public Enums = Enums;
  public TICKETS_PATHS = TICKETS_PATHS;
  public ticketInfoLoading = false;
  public ticketInfo = null;
  public ticketId = null;
  public brigadeInfo = null;
  public ticketHdComments = null;
  public ticketHdCommentsLoading = false;
  public ticketHdCommentsError = null;
  public ticketCommentTypeSelected = 'HD';
  public ticketCommentTypeMode = 'collapsed';
  public ticketAbonentStatusLoading = false;
  public ticketAbonentStatus = null;
  public ticketAbonentStatusError = null;
  public ticketBreadcrumbs = null;
  public ticketGlobalProblems = null;
  public isExistsServices = false;
  public isExistsExtra = false;
  public gpTypeMode = 'collapsed';
  public checklistTicket: IChecklistTicket;
  public checklistAnswers: IChecklistTicketAnswer[] = [];
  public checklistTicketLoading: boolean;
  private destroyed$ = new Subject<void>();

  constructor(
    private ticketViewService: TicketViewService,
    private brigadePopoverService: BrigadePopoverService,
    private route: ActivatedRoute,
    private breadcrumbsService: BreadcrumbsService,
    private checkListsApiService: CheckListsApiService,
    private _location: Location
  ) {}

  get isAnyLoading() {
    return this.checklistTicketLoading || this.ticketInfoLoading;
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.ticketId = params.get('ticket_id');
      this.getTicketInfo(this.ticketId);
    });

    this.moment.updateLocale('ru', {
      months: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
    });
  }

  downloadMedia = (ticket: IChecklistTicket): Observable<HttpResponse<Blob>> => {
    return this.checkListsApiService.downloadMedia(ticket.hd_ticket_number);
  };

  downloadPdf = (ticket: IChecklistTicket): Observable<HttpResponse<Blob>> => {
    return this.checkListsApiService.downloadPdfFromCsv(ticket);
  };

  private getTicketInfo(ticketId) {
    this.ticketInfoLoading = true;
    const ticket$ = this.ticketViewService.getTicket(ticketId).pipe(
      map((res) => res['data']),
      catchError(() => of((this.ticketInfo = null))),
      finalize(() => (this.ticketInfoLoading = false)),
      filter(value => Boolean(value)),
      share(),
      takeUntil(this.destroyed$)
    );

    ticket$.subscribe((ticketInfo) => {
      this.ticketInfo = ticketInfo;
      this.ticketInfo.esas_comments = [];
      this.ticketBreadcrumbs = [];
      this.ticketBreadcrumbs.push({
        text: 'Заявка №' + this.ticketInfo.hd_ticket_number,
        url: null,
      });
      this.breadcrumbsService.updateBreadcrumbs(this.ticketBreadcrumbs);
      this.checkServicesAndExtra();

      if (this.ticketInfo && this.ticketInfo.brigade) {
        this.getBrigadeInfo(this.ticketInfo.brigade.id);
      }

      if (this.ticketInfo.ticket_login && this.ticketInfo.id) {
        this.getTicketAbonentStatus({
          login: this.ticketInfo.ticket_login,
        });
      }

      this.getEsasComments(this.ticketInfo.hd_ticket_number);
      this.getHdComments(this.ticketInfo.hd_ticket_number);

      this.getGlobalProblemData(this.ticketInfo.hd_house_id);

      this.ticketInfoLoading = false;
    });

    ticket$
      .pipe(
        filter(value => Boolean(value)),
        switchMap((checklistTicket) => {
          return checklistTicket.has_checklist
            ? this.checkListsApiService.getAnswers(checklistTicket.id).pipe(
                map(({ data }) => ({
                  checklistAnswers: data,
                  checklistTicket,
                }))
              )
            : of({ checklistTicket, checklistAnswers: [] });
        })
      )
      .subscribe(
        ({ checklistTicket, checklistAnswers }) => {
          this.checklistTicket = checklistTicket;
          this.checklistAnswers = checklistAnswers;
        },
        () => (this.checklistTicket = null),
        () => (this.checklistTicketLoading = false)
      );
  }

  private checkServicesAndExtra() {
    let servicesCountExist = 0;
    let servicesCount = 0;
    let extraCountExist = 0;
    let extraCount = 0;

    forEach(this.ticketInfo.services, (value, key) => {
      value ? servicesCountExist++ : servicesCount++;
    });

    forEach(this.ticketInfo.extra_flags, (value, key) => {
      value ? extraCountExist++ : extraCount++;
    });

    this.isExistsServices = servicesCountExist > 0;
    this.isExistsExtra = extraCountExist > 0;
  }

  private getBrigadeInfo(brigadeId) {
    this.brigadePopoverService.getBrigade(brigadeId).subscribe((response) => {
      this.brigadeInfo = response['data'];
    });
  }

  private getEsasComments(ticketNumber) {
    return this.ticketViewService.getEsasComments(ticketNumber).subscribe((response) => {
      this.ticketInfo.esas_comments = response.data || [];
    });
  }

  private getHdComments(ticketNumber) {
    this.ticketHdCommentsLoading = true;
    this.ticketViewService.getHdComments(ticketNumber).subscribe(
      (response) => {
        this.ticketHdComments = has(response, 'data') ? response['data'] : response;
        this.ticketHdCommentsLoading = false;
        this.ticketHdCommentsError = null;
      },
      (error) => {
        this.ticketHdCommentsError = 'Неизвестная ошибка. Повторите попытку через некоторое время.';
        this.ticketHdCommentsLoading = false;
      }
    );
  }

  private getTicketAbonentStatus(login) {
    this.ticketAbonentStatusLoading = true;
    this.ticketViewService.getHdAbonentStatus(login).subscribe(
      (response) => {
        let code = null;
        if (response.data && response.data.status != null) {
          code = Number(response.data.status);
        }
        this.ticketAbonentStatus = this.mapAbonentStatus(code);
        this.ticketAbonentStatusError = null;
        this.ticketAbonentStatusLoading = false;
      },
      (error) => {
        this.ticketAbonentStatus = null;
        this.ticketAbonentStatusError = 'Неизвестная ошибка. Повторите попытку через некоторое время.';
        this.ticketAbonentStatusLoading = false;
      }
    );
  }

  private mapAbonentStatus(code: number | null | undefined): string | null {
    if (code == null) {
      return null;
    }
    if ([1, 2, 3, 4].indexOf(code) > -1) {
      return 'ONLINE';
    }
    return 'OFFLINE';
  }

  public refreshAbonentStatus(): void {
    if (!this.ticketInfo || !this.ticketInfo.ticket_login || !this.ticketInfo.id) {
      return;
    }
    this.getTicketAbonentStatus({
      login: this.ticketInfo.ticket_login,
    });
  }

  private getGlobalProblemData(houseId) {
    this.ticketViewService.getHdGlobalProblem(houseId).subscribe((response) => {
      this.ticketGlobalProblems = has(response, 'data') ? response['data'] : response;
    });
  }

  public printTicket() {
    window.print();
  }

  public changeTicketCommentType() {
    this.ticketCommentTypeSelected = this.ticketCommentTypeSelected === 'HD' ? 'ESAS' : 'HD';
    this.ticketCommentTypeMode = 'collapsed';
  }

  public changeCommentsMode() {
    this.ticketCommentTypeMode = this.ticketCommentTypeMode === 'collapsed' ? 'expanded' : 'collapsed';
  }

  public addEsasComment(comment) {
    let comments = this.ticketInfo.esas_comments;

    if (comments && !comments.length) {
      comments = [];
    }

    comments.unshift(comment);
  }

  public changeGPMode() {
    this.gpTypeMode = this.gpTypeMode === 'collapsed' ? 'expanded' : 'collapsed';
  }

  public backClicked() {
    this._location.back();
  }

  public ngOnDestroy() {
    this.destroyed$.next();
  }
}
